import React, { useState, useEffect } from "react";
import { getFirestore, collection, query, where, getDocs, orderBy } from "firebase/firestore";
import { getAuth } from "firebase/auth";
import "../styles/MobileWallet.css";

interface WalletItem {
  id: string;
  code: string;
  type: "GIFTCARD" | "VALUE";
  balance: number;
  currency: string;
  expiresAt?: Date;
  createdAt: Date;
  status: "ACTIVE" | "REDEEMED" | "EXPIRED";
}

export function MobileWalletPage() {
  const auth = getAuth();
  const [items, setItems] = useState<WalletItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalBalance, setTotalBalance] = useState(0);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const db = getFirestore();

  useEffect(() => {
    loadWalletItems();
  }, []);

  const loadWalletItems = async () => {
    try {
      setLoading(true);
      setError(null);

      const user = auth.currentUser;
      if (!user) {
        setError("User not logged in");
        return;
      }

      const itemsCollection = collection(db, "wallet_items");
      const q = query(
        itemsCollection,
        where("userId", "==", user.uid),
        orderBy("createdAt", "desc")
      );

      const snapshot = await getDocs(q);
      const walletItems: WalletItem[] = [];
      let total = 0;

      snapshot.forEach((doc) => {
        const data = doc.data();
        const item: WalletItem = {
          id: doc.id,
          code: data.code,
          type: data.type,
          balance: data.balance || 0,
          currency: data.currency || "NOK",
          expiresAt: data.expiresAt?.toDate?.(),
          createdAt: data.createdAt?.toDate?.() || new Date(),
          status: data.status || "ACTIVE",
        };
        walletItems.push(item);

        if (item.status === "ACTIVE") {
          total += item.balance;
        }
      });

      setItems(walletItems);
      setTotalBalance(total);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load wallet");
      console.error("Error loading wallet:", err);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat("no-NO", {
      style: "currency",
      currency: "NOK",
    }).format(amount);
  };

  const formatDate = (date: Date): string => {
    return new Intl.DateTimeFormat("no-NO", {
      year: "numeric",
      month: "short",
      day: "numeric",
    }).format(date);
  };

  const isExpired = (item: WalletItem): boolean => {
    if (!item.expiresAt) return false;
    return new Date() > item.expiresAt;
  };

  return (
    <div className="mobile-wallet">
      {/* Balance Card */}
      <div className="wallet-balance-card">
        <div className="balance-label">Total Balance</div>
        <div className="balance-amount">{formatCurrency(totalBalance)}</div>
        <div className="balance-items">{items.filter((i) => i.status === "ACTIVE").length} items</div>
      </div>

      {/* Refresh Button */}
      <button className="wallet-refresh-btn" onClick={loadWalletItems} disabled={loading}>
        {loading ? "🔄" : "↻"} Refresh
      </button>

      {/* Error State */}
      {error && <div className="wallet-error">{error}</div>}

      {/* Loading State */}
      {loading && (
        <div className="wallet-loading">
          <div className="spinner"></div>
        </div>
      )}

      {/* Wallet Items */}
      {!loading && items.length === 0 && (
        <div className="wallet-empty">
          <div className="empty-icon">💳</div>
          <div className="empty-text">No wallet items yet</div>
          <p>Your gift cards and value items will appear here</p>
        </div>
      )}

      {!loading && items.length > 0 && (
        <div className="wallet-items">
          {items.map((item) => (
            <div
              key={item.id}
              className={`wallet-item ${isExpired(item) ? "expired" : ""} ${
                item.status === "REDEEMED" ? "redeemed" : ""
              }`}
              onClick={() => setExpandedId(expandedId === item.id ? null : item.id)}
            >
              <div className="item-header">
                <div className="item-icon">{item.type === "GIFTCARD" ? "🎁" : "💰"}</div>
                <div className="item-info">
                  <div className="item-type">{item.type === "GIFTCARD" ? "Gift Card" : "Value"}</div>
                  <div className="item-code">{item.code}</div>
                </div>
                <div className="item-balance">{formatCurrency(item.balance)}</div>
              </div>

              {/* Expanded Details */}
              {expandedId === item.id && (
                <div className="item-details">
                  <div className="detail-row">
                    <span className="detail-label">Status</span>
                    <span className={`detail-value status-${item.status.toLowerCase()}`}>
                      {item.status}
                    </span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Created</span>
                    <span className="detail-value">{formatDate(item.createdAt)}</span>
                  </div>
                  {item.expiresAt && (
                    <div className="detail-row">
                      <span className="detail-label">Expires</span>
                      <span className={`detail-value ${isExpired(item) ? "expired" : ""}`}>
                        {formatDate(item.expiresAt)}
                      </span>
                    </div>
                  )}
                  <div className="detail-row">
                    <span className="detail-label">Full Code</span>
                    <span className="detail-value code-value">{item.code}</span>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Action Buttons */}
      {!loading && items.length > 0 && (
        <div className="wallet-actions">
          <button className="action-btn primary">📤 Share Wallet</button>
          <button className="action-btn secondary">💾 Export</button>
        </div>
      )}
    </div>
  );
}
